import React from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { LanguageSelector } from '../pages/pages-managment/pages-details/commun-component/LanguageSelector'

export interface StickyLanguageSelectorProps {
  selectedLanguage: string
  onLanguageChange: (lang: string) => void
  /** Restrict dropdown to these locale codes (e.g. en, fr, de for CMS menu). */
  allowedLanguageCodes?: string[]
  /** default: sticky toolbar (forms). compact: inline block for filter rows / lists. */
  variant?: 'default' | 'compact'
  /** Optional helper text rendered inside the white card. */
  hint?: React.ReactNode
}

function badgeClassForLang(code: string): string {
  switch (code) {
    case 'de':
      return 'badge-light-success text-gray-800'
    case 'fr':
      return 'badge-light-primary text-gray-800'
    case 'en':
      return 'badge-light-warning text-gray-800'
    case 'es':
      return 'badge-light-info text-gray-800'
    case 'ar':
      return 'badge-light-secondary text-gray-800'
    default:
      return 'badge-light text-gray-800'
  }
}

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ar: 'العربية',
}

const StickyLanguageSelector: React.FC<StickyLanguageSelectorProps> = ({
  selectedLanguage,
  onLanguageChange,
  allowedLanguageCodes,
  variant = 'default',
  hint,
}) => {
  const intl = useIntl()
  const visibleCodes = (allowedLanguageCodes?.length ? allowedLanguageCodes : ['fr', 'en', 'de']).slice(0, 5)
  const needsDropdown =
    variant === 'default' ||
    !!(allowedLanguageCodes && allowedLanguageCodes.length > visibleCodes.length)

  return (
    <div
      className={clsx(
        'border border-gray-200 rounded-3 px-4',
        variant === 'default' && 'bg-body py-4 shadow-sm sticky-top mb-6',
        variant === 'compact' && 'bg-light py-3 mb-5',
      )}
      // Keep below fixed header (z=30 creates stacking context for user menu)
      style={variant === 'default' ? { zIndex: 10 } : undefined}
    >
      <div className='d-flex flex-wrap align-items-start justify-content-between gap-4'>
        <div className='d-flex flex-column gap-3 flex-grow-1'>
          <div className='d-flex align-items-center gap-2'>
            <span className='material-symbols-outlined text-primary' style={{fontSize: 18}}>
              language
            </span>
            <span className='fw-semibold text-gray-900 fs-6'>
              {intl.formatMessage({ id: 'ADMIN.COMMON.CONTENT_LANGUAGE_LABEL' })}
            </span>
          </div>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            {visibleCodes.map((code) => {
              const isActive = code === selectedLanguage
              return (
                <button
                  key={code}
                  type='button'
                  className={clsx(
                    'btn btn-sm rounded-pill px-3 py-1',
                    isActive ? 'btn-primary' : 'btn-light-primary',
                  )}
                  onClick={() => onLanguageChange(code)}
                >
                  <span className='fw-semibold text-uppercase me-1'>{code}</span>
                  <span className='opacity-75'>{LANGUAGE_LABELS[code] || code.toUpperCase()}</span>
                </button>
              )
            })}
          </div>
          {needsDropdown && (
            <div className='d-none d-md-block' style={{minWidth: 220, maxWidth: 380, width: '100%'}}>
              <LanguageSelector
                selected={selectedLanguage}
                onChange={onLanguageChange}
                layout='block'
                allowedCodes={allowedLanguageCodes}
              />
            </div>
          )}
          {hint ? <div className='text-muted fs-7'>{hint}</div> : null}
        </div>
        <div
          className={clsx(
            'd-flex align-items-center gap-2 px-3 py-2 rounded-pill fs-8 fw-semibold badge mt-1',
            badgeClassForLang(selectedLanguage),
          )}
        >
          <span>{intl.formatMessage({ id: 'ADMIN.COMMON.EDITING_LOCALE_BADGE' })}</span>
          <strong className='text-uppercase'>{selectedLanguage}</strong>
        </div>
      </div>
    </div>
  )
}

export default StickyLanguageSelector
